import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  softwareUpdateGroupService,
  type Group,
  type CreateGroupRequest,
  type DeviceAlreadyInStaticGroupError,
  type UpdateGroupRequest,
} from '@/lib/software-update-group.service';
import { notificationStore } from '@/stores/notification.store';

export const softwareUpdateGroupKeys = {
  all: ['software-update-groups'] as const,
  lists: () => [...softwareUpdateGroupKeys.all, 'list'] as const,
  list: () => [...softwareUpdateGroupKeys.lists()] as const,
  details: () => [...softwareUpdateGroupKeys.all, 'detail'] as const,
  detail: (id: string) => [...softwareUpdateGroupKeys.details(), id] as const,
};

export function useSoftwareUpdateGroups({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: softwareUpdateGroupKeys.list(),
    queryFn: () => softwareUpdateGroupService.list(),
    enabled,
  });
}

export function useSoftwareUpdateGroup(id: string) {
  return useQuery({
    queryKey: softwareUpdateGroupKeys.detail(id),
    queryFn: () => softwareUpdateGroupService.get(id),
    enabled: !!id,
  });
}

export function useCreateSoftwareUpdateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateGroupRequest) => softwareUpdateGroupService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: softwareUpdateGroupKeys.lists() });
    },
  });
}

export function useUpdateSoftwareUpdateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGroupRequest }) =>
      softwareUpdateGroupService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: softwareUpdateGroupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: softwareUpdateGroupKeys.detail(variables.id) });
    },
  });
}

export function useDeleteSoftwareUpdateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => softwareUpdateGroupService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: softwareUpdateGroupKeys.lists() });
    },
  });
}

export function useAddSoftwareUpdateGroupDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, deviceId }: { id: string; deviceId: string }) =>
      softwareUpdateGroupService.addDevice(id, deviceId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: softwareUpdateGroupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: softwareUpdateGroupKeys.detail(variables.id) });
    },
    onError: (err) => {
      // The API interceptor already surfaces these statuses globally.
      if (isAxiosError(err) && (err.response?.status === 403 || err.response?.status === 503)) {
        return;
      }

      const data = isAxiosError(err)
        ? (err.response?.data as
            | (Partial<DeviceAlreadyInStaticGroupError> & {
                detail?: string;
                error?: { message?: string };
              })
            | undefined)
        : undefined;

      if (isAxiosError(err) && err.response?.status === 409 && data?.group?.name) {
        notificationStore
          .getState()
          .show(
            `Device already belongs to “${data.group.name}”. Remove it from that group before adding it here.`,
            'error',
          );
        return;
      }

      const message =
        data?.error?.message ||
        data?.detail ||
        data?.message ||
        'Failed to add device to group.';
      notificationStore.getState().show(message, 'error');
    },
  });
}

export function useRemoveSoftwareUpdateGroupDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, deviceId }: { id: string; deviceId: string }) =>
      softwareUpdateGroupService.removeDevice(id, deviceId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: softwareUpdateGroupKeys.lists() });
      queryClient.invalidateQueries({ queryKey: softwareUpdateGroupKeys.detail(variables.id) });
    },
  });
}

export type { Group, CreateGroupRequest, UpdateGroupRequest };
